import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { api, ApiError, apiErrorMessage } from "@/lib/api";
import type {
  AiUpdate,
  AiView,
  CredentialKeyView,
  ModelOption,
  RuntimeUpdate,
  RuntimeView,
  SandboxUpdate,
  SandboxView,
  StrategySummary,
} from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import { formatDateTime } from "@/lib/format";

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as const;
const INPUT_CLS =
  "w-full rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400";

export default function SettingsPage() {
  const queryClient = useQueryClient();

  const strategiesQ = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });

  const [slug, setSlug] = useState<string | null>(null);
  useEffect(() => {
    if (slug) return;
    // Prefer an active strategy. The list endpoint returns rows unordered, so
    // taking [0] can land the operator on a retired strategy that looks
    // identical in this form — settings and credentials then save somewhere the
    // runtime never reads, with nothing on screen to say so.
    const list = strategiesQ.data;
    const first = list?.find((s) => s.active) ?? list?.[0];
    if (first) setSlug(first.slug);
  }, [strategiesQ.data, slug]);

  const selected = strategiesQ.data?.find((s) => s.slug === slug) ?? null;

  const runtimeQ = useQuery({
    queryKey: ["settings-runtime", slug],
    enabled: !!slug,
    queryFn: () => api.get<RuntimeView>(`/api/v1/settings/strategies/${slug}/runtime`),
  });

  const credsQ = useQuery({
    queryKey: ["settings-credentials", slug],
    enabled: !!slug,
    queryFn: () => api.get<CredentialKeyView[]>(`/api/v1/settings/strategies/${slug}/credentials`),
  });

  // local form state mirrored from runtimeQ
  const [form, setForm] = useState<RuntimeView | null>(null);
  useEffect(() => {
    if (runtimeQ.data) setForm(runtimeQ.data);
  }, [runtimeQ.data]);

  const saveRuntime = useMutation({
    mutationFn: (body: RuntimeUpdate) =>
      api.put<RuntimeView>(`/api/v1/settings/strategies/${slug}/runtime`, body),
    onSuccess: (data) => {
      setForm(data);
      queryClient.invalidateQueries({ queryKey: ["settings-runtime", slug] });
    },
  });

  // SA 2.9 — AI model settings.
  const aiQ = useQuery({
    queryKey: ["settings-ai", slug],
    enabled: !!slug,
    queryFn: () => api.get<AiView>(`/api/v1/settings/strategies/${slug}/ai`),
  });
  const catalogQ = useQuery({
    queryKey: ["settings-ai-catalog", slug],
    enabled: !!slug,
    queryFn: () => api.get<ModelOption[]>(`/api/v1/settings/strategies/${slug}/ai/catalog`),
  });

  const [ai, setAi] = useState<AiView | null>(null);
  useEffect(() => {
    if (aiQ.data) setAi(aiQ.data);
  }, [aiQ.data]);

  const saveAi = useMutation({
    mutationFn: (body: AiUpdate) =>
      api.put<AiView>(`/api/v1/settings/strategies/${slug}/ai`, body),
    onSuccess: (data) => {
      setAi(data);
      queryClient.invalidateQueries({ queryKey: ["settings-ai", slug] });
    },
  });

  // Sandbox rehearsal settings.
  const sandboxQ = useQuery({
    queryKey: ["settings-sandbox", slug],
    enabled: !!slug,
    queryFn: () => api.get<SandboxView>(`/api/v1/settings/strategies/${slug}/sandbox`),
  });
  const [sandbox, setSandbox] = useState<SandboxView | null>(null);
  useEffect(() => {
    if (sandboxQ.data) setSandbox(sandboxQ.data);
  }, [sandboxQ.data]);

  const saveSandbox = useMutation({
    mutationFn: (body: SandboxUpdate) =>
      api.put<SandboxView>(`/api/v1/settings/strategies/${slug}/sandbox`, body),
    onSuccess: (data) => {
      setSandbox(data);
      queryClient.invalidateQueries({ queryKey: ["settings-sandbox", slug] });
      // The banner reads deployment-level mode; a strategy switching to
      // test_address changes what that banner should say.
      queryClient.invalidateQueries({ queryKey: ["email-mode"] });
    },
  });

  // credential put
  const [credKey, setCredKey] = useState("");
  const [credValue, setCredValue] = useState("");
  const putCred = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.put<void>(`/api/v1/settings/strategies/${slug}/credentials`, { key, value }),
    onSuccess: () => {
      setCredKey("");
      setCredValue("");
      queryClient.invalidateQueries({ queryKey: ["settings-credentials", slug] });
    },
  });
  const deleteCred = useMutation({
    mutationFn: (key: string) =>
      api.del<void>(`/api/v1/settings/strategies/${slug}/credentials/${encodeURIComponent(key)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings-credentials", slug] }),
  });

  if (strategiesQ.isLoading) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (!strategiesQ.data || strategiesQ.data.length === 0) {
    return <div className="text-sm text-[var(--fg-muted)]">No strategies configured.</div>;
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="text-sm text-[var(--fg-muted)]">
            Send window, rate limits, AI models, and integration credentials per strategy.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-[var(--fg-muted)]">Strategy</label>
          <select
            value={slug ?? ""}
            onChange={(e) => setSlug(e.target.value)}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1 text-sm"
          >
            {strategiesQ.data.map((s) => (
              <option key={s.id} value={s.slug}>
                {s.active ? s.name : `${s.name} (inactive)`}
              </option>
            ))}
          </select>
        </div>
      </header>

      {selected && !selected.active ? (
        <div
          role="status"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
          <div>
            <span className="font-semibold">This strategy is inactive.</span> The runtime skips
            it, so anything saved here — including integration credentials — is written but
            never read by the agent or the inbound poller. Switch to an active strategy before
            configuring.
          </div>
        </div>
      ) : null}

      {form ? (
        <Card>
          <CardTitle>Runtime</CardTitle>
          <div className="mb-3 max-w-3xl rounded-md border border-[var(--border)] bg-zinc-50 p-3 text-xs text-[var(--fg-muted)]">
            <p className="font-medium text-[var(--fg)]">These settings belong to the selected strategy.</p>
            <p className="mt-1">
              They are the same values the Strategy editor shows under{" "}
              <span className="font-medium">Email &amp; sending</span>; saving here changes
              them there, and there is no separate global copy. Each strategy has its own
              send window and limits, so switch the strategy above to edit another one.
            </p>
            <p className="mt-1">
              The agent checks the send window each time a step comes due. Inside the
              window it acts; outside it the step is parked until the next open hour, and
              the prospect's <span className="font-medium">Next action</span> shows that time.
              A step that is already parked keeps its time when you change the window here —
              press <span className="font-medium">Run now</span> on the prospect to have it
              re-checked right away. A prospect with a timezone of their own is judged
              against that zone instead of the one below.
            </p>
          </div>
          <form
            className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl"
            onSubmit={(e) => {
              e.preventDefault();
              saveRuntime.mutate({
                defaultSenderName: form.defaultSenderName ?? "",
                greeting: form.greeting ?? "",
                greetingFallback: form.greetingFallback ?? "",
                signature: form.signature ?? "",
                sendWindow: form.sendWindow,
                maxEmailsPerDay: form.maxEmailsPerDay,
                maxEmailsPerDomainPerDay: form.maxEmailsPerDomainPerDay ?? 0,
                domainDailyLimits: form.domainDailyLimits ?? {},
                minDelayBetweenEmailsHours: form.minDelayBetweenEmailsHours,
                approvalMode: form.approvalMode,
                maxNewProspectsPerDay: form.maxNewProspectsPerDay,
                startPacingTimezone: form.startPacingTimezone,
                optOutReimport: form.optOutReimport,
                optOutReimportAlert: form.optOutReimportAlert,
              });
            }}
          >
            <Field label="Sender name">
              <input
                value={form.defaultSenderName ?? ""}
                onChange={(e) => setForm({ ...form, defaultSenderName: e.target.value })}
                className={INPUT_CLS}
              />
              <p className="mt-1 text-xs text-[var(--fg-muted)]">
                The name the agent writes and signs as. Prompts and email templates
                read it as <code>{"{{sender.name}}"}</code>. The address messages
                actually leave from is set by the email provider, not here.
              </p>
            </Field>

            <div className="md:col-span-2 rounded-md border border-[var(--border)] p-3">
              <div className="text-sm font-medium">Greeting and signature</div>
              <p className="mt-0.5 mb-3 text-xs text-[var(--fg-muted)]">
                Filled in once here and used for every phase of this strategy. The
                agent writes the message body only; these two are added around it,
                so what you approve in the queue is what goes out. Leave both empty
                and the agent writes its own opening and sign-off, as before.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Greeting">
                  <input
                    value={form.greeting ?? ""}
                    onChange={(e) => setForm({ ...form, greeting: e.target.value })}
                    aria-label="Greeting"
                    className={INPUT_CLS}
                    placeholder="Hi {{prospect.first_name}},"
                  />
                  <p className="mt-1 text-xs text-[var(--fg-muted)]">
                    Variables: <code>{"{{prospect.first_name}}"}</code>,{" "}
                    <code>{"{{prospect.last_name}}"}</code>,{" "}
                    <code>{"{{prospect.full_name}}"}</code>,{" "}
                    <code>{"{{sender.name}}"}</code>.
                  </p>
                </Field>
                <Field label="Greeting when the name is unknown">
                  <input
                    value={form.greetingFallback ?? ""}
                    onChange={(e) => setForm({ ...form, greetingFallback: e.target.value })}
                    aria-label="Greeting when the name is unknown"
                    className={INPUT_CLS}
                    placeholder="Hi there,"
                  />
                  <p className="mt-1 text-xs text-[var(--fg-muted)]">
                    Used when a prospect was imported without a name. Without it the
                    greeting is left off that message entirely — a name is never
                    guessed and a blank is never sent.
                  </p>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Signature">
                    <textarea
                      value={form.signature ?? ""}
                      onChange={(e) => setForm({ ...form, signature: e.target.value })}
                      aria-label="Signature"
                      className={`${INPUT_CLS} font-mono`}
                      rows={4}
                      placeholder={"Kind regards,\n\n{{sender.name}}"}
                    />
                    <p className="mt-1 text-xs text-[var(--fg-muted)]">
                      Line breaks are kept as typed. The unsubscribe link is added
                      below this automatically.
                    </p>
                  </Field>
                </div>
              </div>
            </div>

            <div className="md:col-span-2 rounded-md border border-[var(--border)] p-3">
              <div className="text-sm font-medium">Sending mode</div>
              <p className="mt-0.5 mb-2 text-xs text-[var(--fg-muted)]">
                Whether a person releases each message the agent writes. Individual
                prospects can override this on their own page.
              </p>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="approvalMode"
                  className="mt-1"
                  checked={form.approvalMode === "REVIEW"}
                  onChange={() => setForm({ ...form, approvalMode: "REVIEW" })}
                />
                <span>
                  Review before sending
                  <span className="block text-xs text-[var(--fg-muted)]">
                    The agent composes each message and waits in the approval queue.
                    You can edit it before it goes.
                  </span>
                </span>
              </label>
              <label className="mt-2 flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="approvalMode"
                  className="mt-1"
                  checked={form.approvalMode === "AUTO"}
                  onChange={() => setForm({ ...form, approvalMode: "AUTO" })}
                />
                <span>
                  Send automatically
                  <span className="block text-xs text-[var(--fg-muted)]">
                    The agent sends on its own schedule with no approval step. You
                    still see every message in the Emails view and can reply or
                    pause a prospect at any time.
                  </span>
                </span>
              </label>
            </div>

            <div className="md:col-span-2 rounded-md border border-[var(--border)] p-3">
              <div className="text-sm font-medium">Previously opted-out addresses</div>
              <p className="mt-0.5 mb-2 text-xs text-[var(--fg-muted)]">
                When someone unsubscribes, the address is remembered for this strategy,
                not just the prospect they were at the time. This decides what happens
                when a list you load later — or a prospect you add by hand — carries
                that address again. A bounced or spam-reported address is always
                dropped, whatever you choose here.
              </p>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="optOutReimport"
                  className="mt-1"
                  checked={form.optOutReimport === "SKIP"}
                  onChange={() => setForm({ ...form, optOutReimport: "SKIP" })}
                />
                <span>
                  Skip the address
                  <span className="block text-xs text-[var(--fg-muted)]">
                    It is not imported. Other addresses on the same row still are, and
                    the import result lists the rows this happened to.
                  </span>
                </span>
              </label>
              <label className="mt-2 flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="optOutReimport"
                  className="mt-1"
                  checked={form.optOutReimport === "ALLOW"}
                  onChange={() => setForm({ ...form, optOutReimport: "ALLOW" })}
                />
                <span>
                  Import it again as a new prospect
                  <span className="block text-xs text-[var(--fg-muted)]">
                    The earlier opt-out is lifted for this strategy and the address will
                    be mailed once the batch is activated. You are treating the new list
                    as fresh consent — check that this is defensible under the rules
                    that apply to your outreach before choosing it.
                  </span>
                </span>
              </label>
              <label className="mt-3 flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.optOutReimportAlert}
                  onChange={(e) => setForm({ ...form, optOutReimportAlert: e.target.checked })}
                />
                <span>
                  Warn me every time this happens
                  <span className="block text-xs text-[var(--fg-muted)]">
                    Records a warning on the Errors page with the batch and row numbers
                    whenever an import or a manual add meets such an address. The import
                    result shows the affected rows either way; this keeps a record you
                    can come back to.
                  </span>
                </span>
              </label>
            </div>

            <Field label="Start new prospects per day">
              <input
                type="number" min={0}
                value={form.maxNewProspectsPerDay ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    maxNewProspectsPerDay: e.target.value === "" ? null : parseInt(e.target.value, 10),
                  })
                }
                placeholder="0 — only start prospects manually"
                className={INPUT_CLS}
              />
              <p className="mt-1 text-xs text-[var(--fg-muted)]">
                How many not-yet-started prospects the agent begins each day, oldest
                first. Follow-ups to conversations already running are not counted
                here — that is the daily email cap below. Leave blank or 0 and
                nothing starts unless you start it.
              </p>
            </Field>

            <Field label="Start pacing timezone">
              <input
                value={form.startPacingTimezone ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    startPacingTimezone: e.target.value === "" ? null : e.target.value,
                  })
                }
                placeholder="blank — follow the send window timezone"
                className={INPUT_CLS}
              />
              <p className="mt-1 text-xs text-[var(--fg-muted)]">
                Which midnight resets the daily allowance above. Leave it blank
                and it follows the send window timezone below, which is normally
                what you want — set it only when new conversations should be
                paced on a different clock from the one that governs sending.
              </p>
            </Field>

            <Field label="Max emails per day">
              <input
                type="number" min={0}
                value={form.maxEmailsPerDay ?? ""}
                onChange={(e) =>
                  setForm({ ...form, maxEmailsPerDay: e.target.value === "" ? null : parseInt(e.target.value, 10) })
                }
                placeholder="blank — no daily ceiling"
                className={INPUT_CLS}
              />
              <p className="mt-1 text-xs text-[var(--fg-muted)]">
                The ceiling for this whole strategy in one day — every prospect
                together, first contacts and follow-ups alike. Not a per-prospect
                number. Only messages that actually went out count; drafts waiting
                in the approval queue and failed sends do not. The count resets at
                midnight in the timezone below. Once the cap is reached the agent
                stops for the day and picks up in the next day's send window —
                nothing is dropped — and a manual reply is refused with a
                try-again-later. Releasing a draft you have already approved is
                never blocked, though it still counts. Leave blank or 0 for no
                ceiling.
              </p>
            </Field>

            <Field label="Max emails per recipient domain per day">
              <input
                type="number" min={0}
                value={form.maxEmailsPerDomainPerDay ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    maxEmailsPerDomainPerDay:
                      e.target.value === "" ? null : parseInt(e.target.value, 10),
                  })
                }
                placeholder="blank — no per-domain limit"
                className={INPUT_CLS}
              />
              <p className="mt-1 text-xs text-[var(--fg-muted)]">
                How many messages may go to any one recipient domain in a day.
                Mail providers judge a sender per receiving domain, so this keeps
                a day's whole budget from landing on one organisation at once.
              </p>
            </Field>

            <div className="md:col-span-2">
              <DomainLimitsEditor
                value={form.domainDailyLimits ?? {}}
                onChange={(next) => setForm({ ...form, domainDailyLimits: next })}
              />
            </div>

            <Field label="Min delay between emails (hours)">
              <input
                type="number" min={0}
                value={form.minDelayBetweenEmailsHours ?? ""}
                onChange={(e) =>
                  setForm({ ...form, minDelayBetweenEmailsHours: e.target.value === "" ? null : parseInt(e.target.value, 10) })
                }
                placeholder="blank — no minimum"
                className={INPUT_CLS}
              />
              <p className="mt-1 text-xs text-[var(--fg-muted)]">
                Quiet period after a message to one prospect before that same
                prospect may receive another. Counted per prospect, not across the
                list — at 48, each individual hears from you at most every two
                days while the strategy keeps sending to everyone else. Manual
                replies are held to it too.
              </p>
            </Field>

            <Field label="Timezone">
              <input
                value={form.sendWindow?.timezone ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sendWindow: { ...(form.sendWindow ?? defaultWindow()), timezone: e.target.value },
                  })
                }
                placeholder="America/New_York"
                className={INPUT_CLS}
              />
              <p className="mt-1 text-xs text-[var(--fg-muted)]">
                IANA zone name the send window hours are read in, and the zone
                whose midnight resets the daily cap. A prospect carrying their own
                timezone overrides this for their send window — so the message
                lands in their working day, not yours — but the daily cap always
                counts on this zone's clock.
              </p>
            </Field>

            <Field label="Window start hour (0-23)">
              <input
                type="number" min={0} max={23}
                value={form.sendWindow?.startHour ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sendWindow: {
                      ...(form.sendWindow ?? defaultWindow()),
                      startHour: e.target.value === "" ? null : parseInt(e.target.value, 10),
                    },
                  })
                }
                className={INPUT_CLS}
              />
              <p className="mt-1 text-xs text-[var(--fg-muted)]">
                First hour sending may happen, included — 9 means from 09:00.
                Defaults to 9 when blank.
              </p>
            </Field>

            <Field label="Window end hour (1-24)">
              <input
                type="number" min={1} max={24}
                value={form.sendWindow?.endHour ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sendWindow: {
                      ...(form.sendWindow ?? defaultWindow()),
                      endHour: e.target.value === "" ? null : parseInt(e.target.value, 10),
                    },
                  })
                }
                className={INPUT_CLS}
              />
              <p className="mt-1 text-xs text-[var(--fg-muted)]">
                Hour sending stops, excluded — 17 means the last message can go at
                16:59, and 24 means run to the end of the day. Defaults to 17 when
                blank. Anything due outside the window waits for the next opening;
                nothing is discarded.
              </p>
            </Field>

            <div className="md:col-span-2">
              <div className="text-xs text-[var(--fg-muted)] mb-1">Send window days</div>
              <p className="mb-2 text-xs text-[var(--fg-muted)]">
                Days the window is open, read in the timezone above. Work due on a
                closed day waits for the next open one. Select none and the agent
                falls back to Monday–Friday.
              </p>
              <div className="flex gap-2 flex-wrap">
                {DAYS.map((d) => {
                  const enabled = (form.sendWindow?.days ?? []).includes(d);
                  return (
                    <button
                      type="button"
                      key={d}
                      onClick={() => {
                        const cur = form.sendWindow?.days ?? [];
                        const next = enabled ? cur.filter((x) => x !== d) : [...cur, d];
                        setForm({
                          ...form,
                          sendWindow: { ...(form.sendWindow ?? defaultWindow()), days: next },
                        });
                      }}
                      className={
                        "rounded-md border px-2.5 py-1 text-xs " +
                        (enabled
                          ? "bg-blue-50 border-blue-300 text-blue-700"
                          : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50")
                      }
                    >{d}</button>
                  );
                })}
              </div>
            </div>

            <div className="md:col-span-2 flex items-center gap-3 pt-2">
              <button
                type="submit"
                className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
                disabled={saveRuntime.isPending}
              >
                {saveRuntime.isPending ? "Saving…" : "Save runtime"}
              </button>
              {saveRuntime.error ? (
                <div className="text-sm text-[var(--danger)]">
                  {saveRuntime.error instanceof ApiError ? `HTTP ${saveRuntime.error.status}` : "Save failed"}
                </div>
              ) : null}
              {saveRuntime.isSuccess ? (
                <div className="text-sm text-[var(--positive)]">Saved.</div>
              ) : null}
            </div>
          </form>
        </Card>
      ) : null}

      {ai ? (
        <Card>
          <CardTitle>AI models</CardTitle>
          <p className="text-xs text-[var(--fg-muted)] mb-3">
            Routed through OpenRouter. Leave a field blank to inherit the deployment default —
            the effective value is shown below. Changes apply to the next AI call; no restart needed.
          </p>

          {/* Guard the shape, not just the absence: `?? []` only covers null and
              undefined, so any non-array body — an error envelope, a proxy's
              HTML page — reached .map and took the whole Settings screen down
              with it. A model picker that cannot list its options is a degraded
              control, not a reason to lose the page. */}
          <datalist id="model-catalog">
            {(Array.isArray(catalogQ.data) ? catalogQ.data : []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label} — ${m.promptUsdPer1k}/${m.completionUsdPer1k} per 1k
              </option>
            ))}
          </datalist>

          <form
            className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl"
            onSubmit={(e) => {
              e.preventDefault();
              saveAi.mutate({
                model: ai.model ?? "",
                fallbackModels: ai.fallbackModels ?? [],
                emailModel: ai.emailModel ?? "",
                analysisModel: ai.analysisModel ?? "",
                extractionModel: ai.extractionModel ?? "",
                documentModel: ai.documentModel ?? "",
                summaryModel: ai.summaryModel ?? "",
              });
            }}
          >
            <div className="md:col-span-2">
              <Field label="Default model (used for every AI task unless overridden below)">
                <input
                  list="model-catalog"
                  value={ai.model ?? ""}
                  placeholder={ai.effective?.EMAIL_GENERATION ?? "anthropic/claude-opus-5"}
                  onChange={(e) => setAi({ ...ai, model: e.target.value })}
                  className={INPUT_CLS}
                />
              </Field>
            </div>

            <div className="md:col-span-2">
              <Field label="Fallback chain (comma-separated, tried in order on transient failure)">
                <input
                  value={(ai.fallbackModels ?? []).join(", ")}
                  placeholder={ai.effective?.FALLBACK_CHAIN ?? ""}
                  onChange={(e) =>
                    setAi({
                      ...ai,
                      fallbackModels: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  className={INPUT_CLS}
                />
              </Field>
            </div>

            {(
              [
                ["emailModel", "Email generation", "EMAIL_GENERATION"],
                ["analysisModel", "Reply analysis", "RESPONSE_ANALYSIS"],
                ["extractionModel", "Qualification extraction", "QUALIFICATION_EXTRACTION"],
                ["documentModel", "Document drafting", "DOCUMENT_GENERATION"],
                ["summaryModel", "Course summary", "COURSE_SUMMARY"],
              ] as const
            ).map(([key, label, purpose]) => (
              <Field key={key} label={`${label} (optional override)`}>
                <input
                  list="model-catalog"
                  value={ai[key] ?? ""}
                  placeholder={ai.effective?.[purpose] ?? ""}
                  onChange={(e) => setAi({ ...ai, [key]: e.target.value })}
                  className={INPUT_CLS}
                />
              </Field>
            ))}

            <div className="md:col-span-2 flex items-center gap-3 pt-2">
              <button
                type="submit"
                className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
                disabled={saveAi.isPending}
              >
                {saveAi.isPending ? "Saving…" : "Save AI models"}
              </button>
              {saveAi.error ? (
                <div className="text-sm text-[var(--danger)]">
                  {saveAi.error instanceof ApiError
                    ? `HTTP ${saveAi.error.status}${saveAi.error.message ? ` — ${saveAi.error.message}` : ""}`
                    : "Save failed"}
                </div>
              ) : null}
              {saveAi.isSuccess ? <div className="text-sm text-[var(--positive)]">Saved.</div> : null}
            </div>
          </form>

          <div className="mt-4 pt-4 border-t border-[var(--border)]">
            <div className="text-xs uppercase tracking-wide text-[var(--fg-muted)] mb-2">
              Currently in effect
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {Object.entries(ai.effective ?? {}).map(([purpose, model]) => (
                <div key={purpose} className="flex justify-between gap-3">
                  <dt className="text-[var(--fg-muted)]">{humanizePurpose(purpose)}</dt>
                  <dd><code>{model || "—"}</code></dd>
                </div>
              ))}
            </dl>
          </div>
        </Card>
      ) : null}

      {sandbox ? (
        <Card>
          <CardTitle>Sandbox rehearsal</CardTitle>
          <p className="text-xs text-[var(--fg-muted)] mb-3">
            How much real infrastructure this strategy touches while the deployment is
            sandboxed. {sandbox.deploymentSandboxed ? null : (
              <span className="text-amber-700">
                This deployment is live, so these settings currently have no effect.
              </span>
            )}
          </p>

          <form
            className="space-y-3 max-w-2xl"
            onSubmit={(e) => {
              e.preventDefault();
              saveSandbox.mutate({
                mode: sandbox.mode,
                testAddress: sandbox.testAddress,
                provider: sandbox.provider,
                autoReply: sandbox.autoReply,
              });
            }}
          >
            <Field label="Mode">
              <select
                value={sandbox.mode}
                onChange={(e) =>
                  setSandbox({ ...sandbox, mode: e.target.value as SandboxView["mode"] })
                }
                className={INPUT_CLS}
              >
                <option value="isolated">Isolated — nothing real, in or out</option>
                <option value="test_address">Test address — real mail, one mailbox only</option>
              </select>
              <p className="mt-1 text-xs text-[var(--fg-muted)]">
                {sandbox.mode === "isolated"
                  ? "Messages are recorded but never delivered, and the inbound poller does not connect to any mail server."
                  : "Every message is really sent, with the recipient rewritten to the address below and tagged per prospect so replies are attributed correctly. Inbound polling is active."}
              </p>
            </Field>

            {sandbox.mode === "test_address" ? (
              <>
                <Field label="Test address">
                  <input
                    type="email"
                    value={sandbox.testAddress ?? ""}
                    onChange={(e) => setSandbox({ ...sandbox, testAddress: e.target.value })}
                    className={INPUT_CLS}
                    placeholder="you@example.com"
                  />
                  <p className="mt-1 text-xs text-[var(--fg-muted)]">
                    Every rehearsal message goes here instead of to the prospect. The CRM
                    still shows each prospect's own address.
                  </p>
                </Field>

                <Field label="Carrier">
                  <select
                    value={sandbox.provider ?? ""}
                    onChange={(e) => setSandbox({ ...sandbox, provider: e.target.value })}
                    className={INPUT_CLS}
                  >
                    <option value="">Select a provider…</option>
                    {sandbox.availableProviders.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-[var(--fg-muted)]">
                    The real provider that carries redirected mail. Required — without it
                    nothing would leave and no reply could arrive.
                  </p>
                </Field>
              </>
            ) : null}

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={sandbox.autoReply}
                onChange={(e) => setSandbox({ ...sandbox, autoReply: e.target.checked })}
                className="mt-0.5"
              />
              <span>
                Auto-reply
                <span className="block text-xs text-[var(--fg-muted)]">
                  The platform composes the prospect's side of each conversation so you can
                  walk a strategy through several phases without writing both halves. Every
                  generated message is marked as simulated.
                </span>
              </span>
            </label>

            {saveSandbox.isError ? (
              <p className="text-xs text-[var(--danger)]">
                {apiErrorMessage(saveSandbox.error, "Could not save sandbox settings.")}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={saveSandbox.isPending}
              className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {saveSandbox.isPending ? "Saving…" : "Save sandbox settings"}
            </button>
          </form>
        </Card>
      ) : null}

      <Card>
        <CardTitle>Integration credentials</CardTitle>
        <p className="text-xs text-[var(--fg-muted)] mb-3">
          Credentials are encrypted with AES-GCM under {`DEALFLOW_CREDENTIAL_KEK`} and never returned in the clear.
          The only key the platform reads from here is <code>imap.password</code>, used by the inbound IMAP poller
          for strategies that declare an <code>email.inbound.imap</code> block. Email-provider API keys are
          deployment configuration, not strategy credentials — they live in <code>.env</code>
          (<code>WOODPECKER_API_KEY</code>, <code>RESEND_API_KEY</code>) and are ignored if set here.
        </p>

        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">
            <tr><th className="text-left py-1">Key</th><th className="text-left py-1">Updated</th><th /></tr>
          </thead>
          <tbody>
            {credsQ.data && credsQ.data.length > 0 ? credsQ.data.map((c) => (
              <tr key={c.key} className="border-t border-[var(--border)]">
                <td className="py-1.5"><code>{c.key}</code></td>
                <td className="py-1.5 text-[var(--fg-muted)]">{formatDateTime(c.updatedAt)}</td>
                <td className="py-1.5 text-right">
                  <button
                    onClick={() => {
                      if (confirm(`Delete credential "${c.key}"? This is destructive.`)) {
                        deleteCred.mutate(c.key);
                      }
                    }}
                    className="text-xs text-[var(--danger)] hover:underline"
                  >Delete</button>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={3} className="py-3 text-[var(--fg-muted)]">No credentials configured.</td></tr>
            )}
          </tbody>
        </table>

        <form
          className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-end pt-4 mt-4 border-t border-[var(--border)]"
          onSubmit={(e) => {
            e.preventDefault();
            if (!credKey || !credValue) return;
            putCred.mutate({ key: credKey, value: credValue });
          }}
        >
          <Field label="Key">
            <input value={credKey} onChange={(e) => setCredKey(e.target.value)} className={INPUT_CLS} placeholder="imap.password" />
          </Field>
          <Field label="Value (write-only)">
            <input type="password" value={credValue} onChange={(e) => setCredValue(e.target.value)} className={INPUT_CLS} />
          </Field>
          <button
            type="submit"
            disabled={!credKey || !credValue || putCred.isPending}
            className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
          >{putCred.isPending ? "Saving…" : "Set credential"}</button>
        </form>
      </Card>
    </div>
  );
}

/** RESPONSE_ANALYSIS → "Reply analysis"; FALLBACK_CHAIN → "Fallback chain". */
function humanizePurpose(purpose: string): string {
  const overrides: Record<string, string> = {
    EMAIL_GENERATION: "Email generation",
    RESPONSE_ANALYSIS: "Reply analysis",
    QUALIFICATION_EXTRACTION: "Qualification extraction",
    DOCUMENT_GENERATION: "Document drafting",
    COURSE_SUMMARY: "Course summary",
    FALLBACK_CHAIN: "Fallback chain",
  };
  if (overrides[purpose]) return overrides[purpose];
  const lower = purpose.toLowerCase().replace(/_/g, " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function defaultWindow() {
  return { startHour: 9, endHour: 17, timezone: "UTC", days: ["MON", "TUE", "WED", "THU", "FRI"] as string[] };
}

/**
 * Per-domain overrides of the default recipient-domain cap (SA 1.13).
 *
 * Rows are held locally so a half-typed domain does not churn the parent form
 * on every keystroke; the parent is told only about rows that are complete and
 * valid. Removing every row clears the overrides, which is the documented way
 * to fall back to the default for all domains.
 */
function DomainLimitsEditor({
  value,
  onChange,
}: {
  value: Record<string, number>;
  onChange: (next: Record<string, number>) => void;
}) {
  const [rows, setRows] = useState<{ domain: string; limit: string }[]>([]);
  const serialized = JSON.stringify(value);

  useEffect(() => {
    setRows(
      Object.entries(JSON.parse(serialized) as Record<string, number>).map(([domain, limit]) => ({
        domain,
        limit: String(limit),
      })),
    );
  }, [serialized]);

  function push(next: { domain: string; limit: string }[]) {
    setRows(next);
    const out: Record<string, number> = {};
    for (const r of next) {
      const domain = r.domain.trim().toLowerCase();
      const limit = parseInt(r.limit, 10);
      if (domain && Number.isFinite(limit) && limit > 0) out[domain] = limit;
    }
    onChange(out);
  }

  return (
    <div className="rounded-md border border-[var(--border)] p-3">
      <div className="text-sm font-medium">Per-domain exceptions</div>
      <p className="mt-0.5 mb-2 text-xs text-[var(--fg-muted)]">
        Domains that should get a different daily allowance than the default
        above. Everything not listed here uses the default.
      </p>

      {rows.length === 0 && (
        <p className="text-xs text-[var(--fg-muted)]">No exceptions — every domain uses the default.</p>
      )}

      {rows.map((row, i) => (
        <div key={i} className="mt-2 flex items-center gap-2">
          <input
            aria-label={`Domain ${i + 1}`}
            value={row.domain}
            placeholder="partner.example"
            onChange={(e) =>
              push(rows.map((r, j) => (i === j ? { ...r, domain: e.target.value } : r)))
            }
            className={INPUT_CLS}
          />
          <input
            aria-label={`Daily limit for domain ${i + 1}`}
            type="number"
            min={1}
            value={row.limit}
            placeholder="25"
            onChange={(e) =>
              push(rows.map((r, j) => (i === j ? { ...r, limit: e.target.value } : r)))
            }
            className={`${INPUT_CLS} w-28`}
          />
          <button
            type="button"
            onClick={() => push(rows.filter((_, j) => j !== i))}
            className="rounded-md border border-[var(--border)] px-2 py-1 text-xs"
          >
            Remove
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() => setRows([...rows, { domain: "", limit: "" }])}
        className="mt-2 rounded-md border border-[var(--border)] px-2 py-1 text-xs"
      >
        Add domain
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-[var(--fg-muted)]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
